import { useEffect, useMemo, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Badge from '../Badge/Badge';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import pageStyles from '../../styles/shared/page.module.css';
import table from '../../styles/shared/table.module.css';
import styles from './GroupMessagesList.module.css';

const STICK_TO_BOTTOM_THRESHOLD_PX = 80;

const SENDER_BADGE = {
  admin: { label: 'Admin', background: 'var(--color-primary)', color: 'var(--color-white)' },
  manager: { label: 'Manager', background: 'var(--color-badge-info-bg)', color: 'var(--color-primary)' },
  host: { label: 'Host', background: 'var(--color-badge-warn-bg)', color: 'var(--color-badge-warn-text)' },
  subscriber: { label: 'Subscriber', background: 'var(--color-icon-success-hover-bg)', color: 'var(--color-success)' },
  staff: { label: 'Staff', background: 'var(--color-badge-neutral-bg)', color: 'var(--color-text-muted)' },
};

function senderBadge(message) {
  if (message.sender_type === 'subscriber') return SENDER_BADGE.subscriber;
  return SENDER_BADGE[message.sender_role] ?? SENDER_BADGE.staff;
}

const ROLE_RANK = { admin: 3, manager: 2, host: 1 };

function canDeleteMessage(currentUserRole, message) {
  if (!currentUserRole) return false;
  if (message.sender_type === 'subscriber') return true;
  const senderRank = message.sender_role ? ROLE_RANK[message.sender_role] : 0;
  return ROLE_RANK[currentUserRole] >= senderRank;
}

function matchesSearch(message, term) {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    message.body.toLowerCase().includes(needle) ||
    (message.sender_name ?? '').toLowerCase().includes(needle)
  );
}

export default function GroupMessagesList({
  messages,
  isLoading,
  currentUserId,
  currentUserRole,
  onDelete,
  isBroadcast = true,
  groupName,
}) {
  const containerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const showToast = useToast();
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  function handleScroll() {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD_PX;
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const oldestFirst = useMemo(() => [...(messages ?? [])].reverse(), [messages]);
  const filtered = useMemo(
    () => (search.trim() ? oldestFirst.filter((m) => matchesSearch(m, search)) : oldestFirst),
    [oldestFirst, search],
  );
  const filteredTable = useMemo(
    () => (search.trim() ? (messages ?? []).filter((m) => matchesSearch(m, search)) : (messages ?? [])),
    [messages, search],
  );

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      showToast('error', 'Messages', errMsg(err, 'Could not delete the message.'));
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) return <p className="textMuted">Loading…</p>;
  if (!messages?.length) return <p className="textMuted">No messages yet.</p>;

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchBox}>
        <IconField iconPosition="left" className={styles.searchField}>
          <InputIcon className="pi pi-search" />
          <InputText
            className="w-full"
            placeholder="Search messages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </IconField>
        {search && (
          <Button
            icon="pi pi-times"
            text
            rounded
            className={styles.clearSearchBtn}
            onClick={() => setSearch('')}
            aria-label="Clear search"
          />
        )}
      </div>

      {isBroadcast ? (
        <div ref={containerRef} className={styles.chat} onScroll={handleScroll}>
          {filtered.length === 0 ? (
            <p className={`textMuted ${styles.noMatches}`}>No messages match “{search}”.</p>
          ) : (
            filtered.map((m) => {
              const isOwn = currentUserId != null && m.sender_user_id === currentUserId;
              const badge = senderBadge(m);
              return (
                <div key={m.id} className={`${styles.row} ${isOwn ? styles.rowOwn : styles.rowOther}`}>
                  <div className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : styles.bubbleOther}`}>
                    {!isOwn && (
                      <div className={styles.bubbleHeader}>
                        <span className={styles.sender}>{m.sender_name || 'Unknown'}</span>
                        <Badge background={badge.background} color={badge.color}>
                          {badge.label}
                        </Badge>
                      </div>
                    )}
                    <p className={styles.body}>{m.body}</p>
                    <span className={styles.timestamp}>{new Date(m.created_at).toLocaleString()}</span>
                    {onDelete && canDeleteMessage(currentUserRole, m) && (
                      <Button
                        icon="pi pi-trash"
                        text
                        rounded
                        className={styles.deleteBtn}
                        onClick={() => setPendingDelete(m)}
                        aria-label="Delete message"
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className={styles.tableScroll}>
          <div className={table.tableWrapper}>
            <DataTable
              value={filteredTable}
              dataKey="id"
              emptyMessage={search ? `No messages match “${search}”.` : 'No messages yet.'}
              responsiveLayout="stack"
              breakpoint="1024px"
            >
              <Column
                header="Group"
                className={table.contactCol}
                style={{ width: '220px' }}
                body={() => <div className={styles.cellWrap}>{groupName || '—'}</div>}
              />
              <Column
                header="Sender"
                className={table.contactCol}
                style={{ width: '200px' }}
                body={(m) => <div className={styles.cellWrap}>{m.sender_name || 'Unknown'}</div>}
              />
              {currentUserRole !== 'host' && (
                <Column
                  header="Role"
                  style={{ width: '150px' }}
                  body={(m) => {
                    const badge = senderBadge(m);
                    return (
                      <div className={styles.cellWrap}>
                        <Badge background={badge.background} color={badge.color}>
                          {badge.label}
                        </Badge>
                      </div>
                    );
                  }}
                />
              )}
              <Column
                header="Message"
                body={(m) => <div className={styles.cellWrap}>{m.body}</div>}
              />
              <Column
                header="Sent"
                className={`${pageStyles.mutedCell} ${table.contactCol}`}
                style={{ width: '230px' }}
                body={(m) => <div className={styles.cellWrap}>{new Date(m.created_at).toLocaleString()}</div>}
              />
              {onDelete && (
                <Column
                  header="Actions"
                  className={table.actionsCol}
                  style={{ width: '100px' }}
                  body={(m) =>
                    canDeleteMessage(currentUserRole, m) && (
                      <div className={table.actions}>
                        <Button
                          icon="pi pi-trash"
                          text
                          className={table.iconDanger}
                          onClick={() => setPendingDelete(m)}
                          aria-label="Delete message"
                        />
                      </div>
                    )
                  }
                />
              )}
            </DataTable>
          </div>
        </div>
      )}

      <Dialog
        header="Delete this message?"
        visible={Boolean(pendingDelete)}
        onHide={() => setPendingDelete(null)}
        style={{ width: '360px' }}
        modal
      >
        <p className="textMuted mb-3">This can&apos;t be undone.</p>
        <div className={styles.confirmActions}>
          <Button
            type="button"
            label="Cancel"
            text
            onClick={() => setPendingDelete(null)}
            disabled={isDeleting}
          />
          <Button
            type="button"
            label="Delete"
            severity="danger"
            onClick={handleConfirmDelete}
            loading={isDeleting}
          />
        </div>
      </Dialog>
    </div>
  );
}
